import React from 'react';
import { CustomizedSwitch, ToolInfo } from 'components';
import { evalModeEnums, firstTrialResponseEnums, firstTrialResponseList, goalDataTypeEnums, masteryEvalTypeEnums } from 'utils';

/**
 * First-trial criteria — shared by the mastery, baseline and maintenance forms (goal, target and library).
 *
 * PERCENT goals only. When `firstTrialOnly` is on, each session/day is judged solely by its first trial:
 * it counts when that first response is the selected one (Correct / Incorrect).
 *   - Consecutive: every session/day in the window must count.
 *   - Total:       at least `firstTrialCount` of them must count.
 * The regular % settings (direction / count) are kept untouched, so switching it off restores them.
 */

export const isFirstTrialAvailable = (goal) => goal?.dataType === goalDataTypeEnums.PERCENT;

export const isFirstTrialActive = (goal, criteria) => isFirstTrialAvailable(goal) && !!criteria?.firstTrialOnly;

export const isTotalEvalType = (criteria) => criteria?.evalType === masteryEvalTypeEnums.TOTAL;

/**
 * Draft patch that flips first-trial mode, defaulting the response to Correct. First trial is a per-session rule,
 * so turning it on also switches the evaluation to sessions (the API rejects it together with BY_DAY).
 */
export const toggleFirstTrial = (criteria) => {
    const firstTrialOnly = !criteria?.firstTrialOnly;
    return {
        firstTrialOnly,
        firstTrialResponse: criteria?.firstTrialResponse || firstTrialResponseEnums.CORRECT,
        ...(firstTrialOnly ? { evalMode: evalModeEnums.BY_SESSION } : {}),
    };
};

/** "correct" / "incorrect" for sentences; `opposite` gives the response that counts as a miss. */
export const firstTrialResponseText = (criteria, opposite = false) => {
    const isIncorrect = criteria?.firstTrialResponse === firstTrialResponseEnums.INCORRECT;
    return isIncorrect !== opposite ? 'incorrect' : 'correct';
};

/**
 * The count shown for Total. Never set means "the whole window", so the window size is shown. While the user is
 * typing the value is shown as is — "" (cleared) and too-large values included — so the field never snaps back.
 */
export const resolveFirstTrialCount = (count, windowSize) => {
    if (count === '') return '';
    if (count === undefined || count === null) return windowSize ?? '';
    return count;
};

/** A Total count is valid when it is filled in and fits the window. */
export const isFirstTrialCountValid = (count, windowSize) => {
    if (count === undefined || count === null) return true;
    return typeof count === 'number' && count >= 1 && (!windowSize || count <= windowSize);
};

export const FirstTrialSwitch = ({ goal, checked, disabled, onToggle }) => {
    if (!isFirstTrialAvailable(goal)) return null;

    return (
        <div className="switcher-style" style={{ gap: 4 }}>
            <ToolInfo
                text={
                    'Judge each session only by its first trial — it counts when the first response is the one selected. ' +
                    'Consecutive: every session must count. Total: at least the given number of sessions must count.'
                }
            />
            <p style={{ width: 'auto', padding: 0, opacity: disabled ? 0.5 : 1 }}>First Trial</p>
            <CustomizedSwitch disabled={disabled} checked={!!checked} handleClick={onToggle} />
        </div>
    );
};

export const FirstTrialResponseSelect = ({ value, disabled, onChange }) => (
    <div className="mastery-percent-input-box" style={{ width: 120, padding: '7px 10px' }}>
        <select
            disabled={disabled}
            name="firstTrialResponse"
            value={value || firstTrialResponseEnums.CORRECT}
            onChange={(e) => onChange(e.target.value)}
        >
            {firstTrialResponseList.map((i) => (
                <option key={i.value} value={i.value}>
                    {i.label}
                </option>
            ))}
        </select>
    </div>
);

/** "at least [X] times" — the Total count, kept within 1..max. */
export const FirstTrialCountInput = ({ value, max, disabled, error, onChange, onBlur, width = 100 }) => (
    <>
        <p className="target-mastery-info">at least</p>
        <div className="mastery-percent-input-box" style={{ width, padding: '7px 8px', ...(error ? { borderColor: '#FE7070' } : {}) }}>
            <input
                disabled={disabled}
                type="number"
                name="firstTrialCount"
                min={1}
                max={max}
                value={value}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return onChange('');
                    const next = Math.floor(+raw);
                    onChange(next < 1 ? 1 : next);
                }}
                onBlur={onBlur}
            />
            <p>times</p>
        </div>
    </>
);

/**
 * The mastery / baseline sentence in first-trial mode — replaces the "value is [direction] [count %]" part:
 * "<label> [Correct] (at least [X] times)". `onChange` receives a draft patch.
 */
export const FirstTrialCriteriaFields = ({ label, criteria, disabled, onChange }) => (
    <>
        <p className="target-mastery-info">{label}</p>
        <FirstTrialResponseSelect
            disabled={disabled}
            value={criteria?.firstTrialResponse}
            onChange={(value) => onChange({ firstTrialResponse: value })}
        />
        {isTotalEvalType(criteria) && (
            <FirstTrialCountInput
                disabled={disabled}
                max={criteria?.evalModeCount}
                error={!isFirstTrialCountValid(criteria?.firstTrialCount, criteria?.evalModeCount)}
                value={resolveFirstTrialCount(criteria?.firstTrialCount, criteria?.evalModeCount)}
                onChange={(value) => onChange({ firstTrialCount: value })}
            />
        )}
    </>
);

/**
 * One side of a maintenance step in first-trial mode — replaces "[direction] [value %]":
 *   next: "first response is correct (at least [X] times)"
 *   back: "first response is incorrect (at least [X] times)" — i.e. the misses
 * `kind` is 'next' or 'back'; the step's "for [N] session(s)" part stays in the form.
 */
export const FirstTrialStepCondition = ({ criteria, step, kind, disabled, error, onCountChange }) => {
    const isBack = kind === 'back';
    const countKey = isBack ? 'firstTrialBackCount' : 'firstTrialNextCount';
    const across = isBack ? step?.moveBackAcross : step?.moveNextAcross;

    return (
        <>
            <p className="target-mastery-info">first response is {firstTrialResponseText(criteria, isBack)}</p>
            {isTotalEvalType(criteria) && (
                <FirstTrialCountInput
                    width={95}
                    disabled={disabled}
                    error={error || !isFirstTrialCountValid(step?.[countKey], across)}
                    max={across || undefined}
                    value={resolveFirstTrialCount(step?.[countKey], across)}
                    onChange={(value) => onCountChange(countKey, value)}
                />
            )}
        </>
    );
};

/**
 * Maintenance steps keep their % fields even in first-trial mode, where they are hidden, and the API still
 * validates them (Next above Back). A step created in first-trial mode never had them filled in, so give it
 * placeholders that pass — they only matter if first-trial mode is switched off later, and can be edited then.
 */
export const withFirstTrialStepDefaults = (step) => ({
    ...step,
    // A cleared count means "the whole window" — send nothing rather than "".
    firstTrialNextCount: step?.firstTrialNextCount === '' ? undefined : step?.firstTrialNextCount,
    firstTrialBackCount: step?.firstTrialBackCount === '' ? undefined : step?.firstTrialBackCount,
    moveNextIfValue: step?.moveNextIfValue > 0 ? step.moveNextIfValue : 80,
    moveBackIfValue: step?.moveBackIfValue > 0 ? step.moveBackIfValue : 50,
});

/** Step validation in first-trial mode: only the schedule and the window sizes are visible / required. */
export const validateFirstTrialStep = (step, criteria) => {
    if (!(step?.showTargetEvery > 0)) return { valid: false, field: 'showTargetEvery' };
    if (!(step?.moveNextAcross > 0)) return { valid: false, field: 'moveNextAcross' };
    if (!(step?.moveBackAcross > 0)) return { valid: false, field: 'moveBackAcross' };
    // The counts are only shown (and only used) for Total.
    if (isTotalEvalType(criteria)) {
        if (!isFirstTrialCountValid(step?.firstTrialNextCount, step?.moveNextAcross)) return { valid: false, field: 'firstTrialNextCount' };
        if (!isFirstTrialCountValid(step?.firstTrialBackCount, step?.moveBackAcross)) return { valid: false, field: 'firstTrialBackCount' };
    }
    return { valid: true };
};
